import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthService } from '../services/api/auth/AuthService';
import { Api, ApiLogin } from '../services/api/axios-config';
import { EmpresaStatusService } from '../services/api/Venda/EmpresaStatusService';

interface IAuthContextData {
  logout: () => void;
  isAuthenticated: boolean;
  isSistema: boolean;
  // true quando o login foi válido mas a empresa está desativada no painel de
  // controle — o App.tsx mostra a tela de suporte em vez de entrar no PDV
  // (igual ao FrontSIAC).
  empresaInativa: boolean;
  login: (login: string, senha: string) => Promise<string | void>;
  sistema: (sistemas: string) => void;
  logoutsistema: () => void;
  // Caixa (PDV) escolhido pelo operador ao entrar no PDV — ver
  // DialogSelecionarCaixa. Fica em memória/localStorage do mesmo jeito que
  // o token, e viaja em todo request pro backend via header `idconta`.
  idContaSelecionada?: string;
  selecionarCaixa: (idconta: string) => void;
}

const AuthContext = createContext({} as IAuthContextData);
const LOCAL_STORAGE_KEY__ACCESS_TOKEN = 'APP_ACCESS_TOKEN';
const LOCAL_STORAGE_KEY__IDCONTA_SELECIONADA = 'PDV_IDCONTA_SELECIONADA';

interface IAuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string>();
  const [accessSistema, setAccessSistema] = useState<string>();
  const [idContaSelecionada, setIdContaSelecionada] = useState<string>();
  const [empresaInativa, setEmpresaInativa] = useState(false);

  // Confere se a empresa continua ativa e, se não estiver, derruba a sessão —
  // chamado tanto num login novo quanto ao restaurar uma sessão salva
  // (useEffect abaixo), pra uma empresa desativada enquanto alguém já estava
  // logado também ser barrada no próximo carregamento do PDV.
  const verificarEmpresaAtiva = useCallback(async () => {
    const ativa = await EmpresaStatusService.verificarAtiva();
    if (!ativa) {
      localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
      ApiLogin.defaults.headers.authorization = null;
      Api.defaults.headers.authorization = null;
      setAccessToken(undefined);
      setEmpresaInativa(true);
    }
    return ativa;
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
    if (token) {
      let parsedToken: string;
      try {
        parsedToken = JSON.parse(token);
      } catch {
        parsedToken = token;
      }
      setAccessToken(parsedToken);
      ApiLogin.defaults.headers.authorization = `Bearer ${parsedToken}`;
      Api.defaults.headers.authorization = `Bearer ${parsedToken}`;
      // Precisa do token já nos headers antes dessa chamada.
      verificarEmpresaAtiva();
    } else {
      setAccessToken(undefined);
    }

    const idconta = localStorage.getItem(LOCAL_STORAGE_KEY__IDCONTA_SELECIONADA);
    if (idconta) {
      setIdContaSelecionada(idconta);
      Api.defaults.headers.idconta = idconta;
    }
  }, [verificarEmpresaAtiva]);

  const handleLogin = useCallback(async (login: string, senha: string) => {
    const result = await AuthService.auth(login, senha);

    if (result instanceof Error) {
      throw new Error(result.message);
    } else {
      const authorization = `Bearer ${result.accessToken}`;
      ApiLogin.defaults.headers.authorization = authorization;
      Api.defaults.headers.authorization = authorization;
      localStorage.setItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN, JSON.stringify(result.accessToken));
      setEmpresaInativa(false);

      // Só marca como logado depois de confirmar a empresa, pra o PDV não
      // aparecer nem por um instante pra empresa desativada.
      const ativa = await EmpresaStatusService.verificarAtiva();
      if (!ativa) {
        localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
        ApiLogin.defaults.headers.authorization = null;
        Api.defaults.headers.authorization = null;
        setEmpresaInativa(true);
        return;
      }

      setAccessToken(result.accessToken);
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
    setAccessToken(undefined);
    ApiLogin.defaults.headers.authorization = null;
    Api.defaults.headers.authorization = null;
    setAccessSistema('');
    setEmpresaInativa(false);

    localStorage.removeItem(LOCAL_STORAGE_KEY__IDCONTA_SELECIONADA);
    Api.defaults.headers.idconta = null;
    setIdContaSelecionada(undefined);
  }, []);

  const handleSelecionarCaixa = useCallback((idconta: string) => {
    localStorage.setItem(LOCAL_STORAGE_KEY__IDCONTA_SELECIONADA, idconta);
    Api.defaults.headers.idconta = idconta;
    setIdContaSelecionada(idconta);
  }, []);

  const handleSistema = useCallback((sistemas: string) => {
    setAccessSistema(sistemas);
  }, []);

  const handleLogoutsistema = useCallback(() => {
    setAccessSistema('');
  }, []);

  const isAuthenticated = useMemo(() => !!accessToken, [accessToken]);
  const isSistema = useMemo(() => !!accessSistema, [accessSistema]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isSistema,
        empresaInativa,
        sistema: handleSistema,
        logoutsistema: handleLogoutsistema,
        login: handleLogin,
        logout: handleLogout,
        idContaSelecionada,
        selecionarCaixa: handleSelecionarCaixa,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
